import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Plus, RefreshCw, Building2, ChevronDown } from 'lucide-react';
import AdminLayout from '../../../components/Admin/Layout/AdminLayout';
import ModuleLayout from '../../../layouts/ModuleLayout';
import DiscountList from '../../../components/Admin/Promotions/Discounts/DiscountList';
import { useAuth } from '../../../context/AuthContext';
import branchService from '../../../services/branchService';

/**
 * Discounts Page
 *
 * Manage discounts that can be applied to:
 * - Individual items
 * - Categories
 * - Entire bills
 */
export default function DiscountsPage() {
  const [componentActions, setComponentActions] = useState(null);
  const location = useLocation();
  const { user } = useAuth();

  const isOwner = user?.userType === 'SUPER_USER';
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(null);

  // Detect if accessed from CRM module
  const isCRMRoute = location.pathname.startsWith('/crm');
  const Layout = isCRMRoute ? ModuleLayout : AdminLayout;

  useEffect(() => {
    if (!isOwner) return;
    branchService.getAllBranches().then(result => {
      if (result && result.length > 1) setBranches(result);
    }).catch(console.error);
  }, [isOwner]);

  const handleActionsReady = useCallback((actions) => {
    setComponentActions(actions);
  }, []);

  return (
    <Layout>
      <div className="flex flex-col h-full">
        {/* Sticky Header */}
        <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between sticky top-0 z-10">
          <h1 className="text-lg font-bold text-slate-900">Discount Management</h1>

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            {/* Branch Dropdown for SUPER_USER */}
            {branches.length > 1 && (
              <div className="relative">
                <Building2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <select
                  value={selectedBranchId ?? ''}
                  onChange={(e) => setSelectedBranchId(e.target.value || null)}
                  className="pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500 appearance-none cursor-pointer"
                >
                  <option value="">All Branches</option>
                  {branches.map(b => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
                <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              </div>
            )}

            {/* Refresh — shown whenever the child has mounted and provided actions */}
            {componentActions?.onRefresh && (
              <button
                onClick={componentActions.onRefresh}
                className="p-1.5 rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-200"
              >
                <RefreshCw size={16} />
              </button>
            )}

            {/* Add Discount — only shown when the child grants ADD permission via renderActions */}
            {componentActions?.onAdd && (
              <button
                onClick={componentActions.onAdd}
                className="px-3 py-1.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center gap-1.5 text-sm font-medium"
              >
                <Plus size={16} />
                Add Discount
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <DiscountList
            renderActions={handleActionsReady}
            selectedBranchId={selectedBranchId}
            isOwner={isOwner}
          />
        </div>
      </div>
    </Layout>
  );
}